"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect } from "react";
declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: {
        url: string;
      }) => void;
    };
  }
}
export default function CoreServices() {
  const {
    ref: servicesRef,
    isVisible: servicesVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  const handleCalendlyClick = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/mfloresdo/30min"
      });
    }
  };
  const serviceCategories = [{
    title: "Hormone & Testosterone Optimization",
    description: "For men with symptoms of low or suboptimal testosterone, including fatigue, low libido, poor recovery, strength decline, and muscle loss.",
    includes: ["Testosterone optimization", "Hormone lab interpretation", "Ongoing dose adjustment", "Safety monitoring"],
    plan: "Available in all plans",
    link: "https://ironpeakmenshealth.com/testosterone-replacement-therapy-trt/"
  }, {
    title: "Weight Loss & Metabolic Health",
    description: "For men struggling with stubborn belly fat, appetite control, insulin resistance, poor body composition, or weight regain despite effort.",
    includes: ["Metabolic lab review", "GLP-1 access when appropriate", "Body composition strategy", "Long-term weight maintenance support"],
    plan: "Ascent & Summit",
    link: "https://ironpeakmenshealth.com/medical-weight-loss-for-men-glp-1-therapy-ironpeak-mens-health/",
    note: "Medication billed separately"
  }, {
    title: "Sexual Health & Performance",
    description: "For men dealing with ED, low libido, confidence issues, or performance concerns. We look at hormones, vascular health, metabolism, and lifestyle factors.",
    includes: ["ED treatment options", "Libido evaluation", "Hormone and metabolic review", "Performance-focused support"],
    plan: "Add-on to any plan",
    link: "https://ironpeakmenshealth.com/erectile-dysfunction-treatment-in-texas/",
    note: "Medication billed separately"
  }, {
    title: "Recovery, Energy & Injectable Wellness",
    description: "For men who want additional support for energy, recovery, cellular health, and performance when clinically appropriate.",
    includes: ["NAD+ therapy", "Vitamin B12 injections", "Peptide therapy options", "Recovery-focused protocols"],
    plan: "Ascent & Summit",
    link: "#",
    note: "Some medications or peptides may be billed separately"
  }, {
    title: "Longevity & Advanced Optimization",
    description: "For men who want a more comprehensive approach to health-span, cognition, cardiometabolic risk, performance, and aging well.",
    includes: ["Advanced lab review", "Cardiometabolic risk support", "Cognitive performance support", "Longevity-focused strategy"],
    plan: "Summit",
    link: "https://ironpeakmenshealth.com/longevity-medicine-for-men-in-texas/"
  }, {
    title: "Lab Coordination & Medical Monitoring",
    description: "Physician-ordered labs through LabCorp or Quest, interpreted in clinical context and used to guide treatment decisions over time.",
    includes: ["Clinically relevant lab orders", "Trend monitoring", "Safety follow-up", "Plan adjustments based on data"],
    plan: "Available in all plans",
    link: "#"
  }];
  return <div className="bg-background py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={servicesRef} className={`transition-all duration-700 ${servicesVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-foreground font-display text-3xl font-bold lg:text-4xl">
              Services That Fit Into Your Plan
            </h2>

            <div className="mx-auto mt-4 flex justify-center">
              <Separator className="bg-muted-light h-px w-24" />
            </div>

            <p className="mt-5 text-base leading-relaxed text-muted-dark md:text-lg">
              IronPeak does not sell one-size-fits-all treatments. These are clinical tools we may use after reviewing your symptoms, labs, goals, and medical history.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {serviceCategories.map(service => <Card key={service.title} className="border-muted-light shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl leading-tight">
                    {service.title}
                  </CardTitle>

                  <span className="mt-2 inline-block w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    {service.plan}
                  </span>
                </CardHeader>

                <CardContent className="space-y-5">
                  <p className="text-foreground text-sm leading-relaxed">
                    {service.description}
                  </p>

                  <ul className="space-y-2">
                    {service.includes.map(item => <li key={item} className="flex items-start gap-2">
                        <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span className="text-sm text-muted-dark">{item}</span>
                      </li>)}
                  </ul>

                  {service.note && <p className="text-xs italic text-muted-foreground">
                      {service.note}
                    </p>}

                  <div className="flex flex-wrap items-center gap-4">
                    {service.link !== "#" && <a href={service.link} className="text-primary inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary/80">
                        Learn more
                        <ArrowRight className="h-4 w-4" />
                      </a>}
                  </div>
                </CardContent>
              </Card>)}
          </div>

          <div className="mt-10 flex justify-center">
            <a href="/pricing" className="inline-flex items-center gap-1 text-primary font-medium">
              View all pricing plans
              <ArrowRight className="h-4 w-4 shrink-0" />
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-16 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={ctaRef} className={`transition-all duration-700 ${ctaVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Card className="border-muted-light shadow-soft bg-primary-light-bg">
            <CardContent className="flex flex-col items-center justify-center space-y-6 py-12 text-center">
              <h2 className="text-foreground font-display text-3xl font-bold lg:text-4xl">
                Not Sure Which Service You Need?
              </h2>

              <p className="text-foreground max-w-3xl text-base leading-relaxed md:text-lg">
                You do not need to choose between TRT, GLP-1 therapy, NAD+, peptides, or supplements on your own. Start with a medical consultation. We review your symptoms, labs, goals, and medical history, then recommend a plan only if it makes clinical sense.
              </p>

              <Button onClick={handleCalendlyClick} className="inline-flex h-12 items-center justify-center rounded-sm bg-accent px-6 py-3 font-semibold text-accent-foreground transition-all duration-150 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-0.5 hover:bg-accent-hover hover:shadow-medium active:translate-y-0 active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40">
                Schedule a Consultation
              </Button>

              <p className="text-foreground/80 text-sm text-center">
                Texas-based telemedicine. Ongoing follow-up. Evidence-based care.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}